import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/joy";
import { ApiCompetitionDetail } from "griptonite-api-client";
import { useEffect } from "react";
import { FormProvider, useController, useForm } from "react-hook-form";

import { useCompetitionQuery, useUpdateCompetitionMutation } from "@/hooks/competitions";
import { useTaskSetForCompetitionQuery } from "@/hooks/tasks";
import { useCurrentVenueUuid } from "@/hooks/venues";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import BottomButtons from "./BottomButtons";
import { useUnhidePreviousTabWarningsOnMount } from "./getPublishWarnings";
import PublishWarningsAlert from "./PublishWarningsAlert";

const CompetitionCircuits = ({ competitionUuid }: { competitionUuid: string }) => {
  const { data: competition } = useCompetitionQuery(competitionUuid);
  const venueUuid = useCurrentVenueUuid();

  useUnhidePreviousTabWarningsOnMount(competitionUuid, "categories", "details");

  const compAutoRouteGroupMode = competition?.autoRouteGroupMode || null;
  const compHasDynamicRouteNumbering = competition?.settings?.hasDynamicRouteNumbering || false;
  const methods = useForm({
    defaultValues: {
      autoRouteGroupMode: compAutoRouteGroupMode,
      dynamicNumbering: compHasDynamicRouteNumbering,
    },
  });

  useEffect(() => {
    methods.reset({
      autoRouteGroupMode: compAutoRouteGroupMode,
      dynamicNumbering: compHasDynamicRouteNumbering,
    });
  }, [compAutoRouteGroupMode, compHasDynamicRouteNumbering, methods]);

  const { field: autoRouteGroupModeField } = useController({
    name: "autoRouteGroupMode",
    control: methods.control,
  });

  const mutation = useUpdateCompetitionMutation(
    competitionUuid,
    venueUuid,
    competition?.league?.uuid,
  );

  const onSubmit = async ({
    autoRouteGroupMode,
    dynamicNumbering,
  }: {
    autoRouteGroupMode: ApiCompetitionDetail["autoRouteGroupMode"];
    dynamicNumbering?: boolean;
  }) => {
    await mutation.mutateAsync({
      autoRouteGroupMode,
      settings: {
        hasDynamicRouteNumbering: autoRouteGroupMode === "PER_CATEGORY" && dynamicNumbering,
      },
    });
  };

  useTaskSetForCompetitionQuery(competitionUuid);

  return (
    <Stack
      flex={1}
      sx={{ overflowY: "auto" }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <Box sx={{ padding: 1.5 }}>
        <FormProvider {...methods}>
          <Box
            sx={
              methods.formState.isSubmitted && !methods.formState.isSubmitSuccessful
                ? { marginBottom: 1.5 }
                : {}
            }
          >
            <FormErrorAlert />
          </Box>
        </FormProvider>
        <PublishWarningsAlert tab="circuits" competition={competition} />
        <Typography level="h3" sx={{ marginBottom: 2 }}>
          Choose an option:
        </Typography>
        <FormProvider {...methods}>
          <RadioGroup
            value={autoRouteGroupModeField.value}
            onChange={(e) => autoRouteGroupModeField.onChange((e.target as HTMLInputElement).value)}
            name="radio-buttons-group"
          >
            <Radio value="SINGLE" label="Everyone climbs the same routes" />
            <FormHelperText>
              The comp will have just one circuit containing all the routes.
            </FormHelperText>
            <Radio value="PER_CATEGORY" label="One circuit per category" />
            <FormHelperText>
              Each category will have its own circuit, so that you can select which routes are tried
              by which climbers.
            </FormHelperText>
          </RadioGroup>
          {autoRouteGroupModeField.value === "PER_CATEGORY" ? (
            <Box sx={{ marginLeft: 4 }}>
              <Accordion
                sx={{
                  ".MuiAccordionSummary-button": {
                    flexDirection: "row-reverse",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    marginTop: 1.5,
                    gap: 0.25,
                    marginLeft: -0.75,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  },
                  ".MuiAccordionDetails-root": { marginTop: 1.5 },
                }}
              >
                <AccordionSummary>
                  <Typography level="title-md">Advanced settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CheckboxInput name="dynamicNumbering" label="Enable dynamic route numbering" />
                  <FormHelperText>
                    Dynamic numbering lets you give the same route a different number for each
                    category. For example, the same route can be #3 for Men and #4 for Women. <br />{" "}
                    NOTE: If selected, any number printed on the tag will be ignored and you will
                    need to select your own route numbers.
                  </FormHelperText>
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : null}
        </FormProvider>
      </Box>
      <Divider />
      <Box sx={{ padding: 1.5, flex: 1 }}>
        <Typography level="h3" sx={{ marginBottom: 2 }}>
          Competition set
        </Typography>
      </Box>
      <Box sx={{ padding: 1.5 }}>
        <BottomButtons
          competition={competition}
          formState={methods.formState}
          nextLink={{
            to: "/gym/$venueUuid/events/$competitionUuid/profile/scoring",
            from: "/gym/$venueUuid/events/$competitionUuid/profile/circuits",
          }}
          prevLink={{
            to: "/gym/$venueUuid/events/$competitionUuid/profile/categories",
            from: "/gym/$venueUuid/events/$competitionUuid/profile/circuits",
          }}
        />
      </Box>
    </Stack>
  );
};

export default CompetitionCircuits;
